"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import SubmenuLeftBottom from "./SubmenuLeftBottom";
import SubmenuChevron from "./SubmenuChevron";

interface InsightCard {
  id: number;
  title: string;
  description?: string | null;
  url: string;
}

interface InsightLink {
  id: number;
  href: string;
  label: string;
  hover?: string | null;
  target?: string | null;
  isExternal?: boolean;
  cards?: InsightCard[];
}

interface ResponsiveImages {
  desktop?: string;
  ipad?: string;
  mobile?: string;
}

interface InsightLeftProps {
  links?: InsightLink[];
  bottomImages?: ResponsiveImages;
}

// One card column (Blogs, Whitepapers, ...). Collapses to a tappable row on
// mobile; from lg up the cards are always shown.
// Open state lives in InsightLeft so only one column is expanded at a time.
const InsightColumn = ({
  link,
  isOpen,
  onToggle,
}: {
  link: InsightLink;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const heading = (
    <span className="text-subtitle-3 font-bold text-[var(--color-primary-text)]">
      {link.label}
    </span>
  );

  return (
    <div className="w-full sm:w-[48%] xl:flex-1 flex flex-col gap-[16px] lg:gap-[16px_24px] pb-[16px] border-b border-[var(--color-blue-100)] lg:pb-0 lg:border-0">
      <div
        className="flex items-center justify-between gap-[12px] lg:block cursor-pointer lg:cursor-auto"
        onClick={onToggle}
      >
        <div className="flex flex-col gap-[8px] lg:gap-[12px]">
          {/* The mobile row toggles rather than navigates, so the title is
              plain text there and a link from lg up. */}
          <span className="lg:hidden">{heading}</span>
          <Link
            href={link.href || "#!"}
            target={link.isExternal ? link.target || "_blank" : undefined}
            rel={link.isExternal ? "noopener noreferrer" : undefined}
            className="hidden lg:block"
          >
            {heading}
          </Link>
          {link.hover && (
            <p className="text-subtitle-4 font-semibold text-[var(--color-secondary-text)] opacity-[70%]">
              {link.hover}
            </p>
          )}
        </div>
        {link.cards && link.cards.length > 0 && (
          <SubmenuChevron
            isOpen={isOpen}
            className="lg:hidden text-[var(--color-primary-text)]"
          />
        )}
      </div>
      <ul
        className={`${isOpen ? "flex" : "hidden"} lg:flex flex-col gap-[16px]`}
      >
        {link.cards?.map((card) => (
          <li key={card.id} className="relative overflow-hidden">
            <Link
              href={card.url || "#!"}
              className="relative overflow-hidden flex flex-col gap-[8px] xl:p-[17px] p-[14px] border border-[#E6F3FD] bg-white group transition-all duration-300 ease-in-out hover:border-[#B0D9F7] before:absolute before:content-[''] before:w-[300px] before:h-[200px] before:top-[-5px] before:right-0 before:bg-[url('/images/insight-card-dots.svg')] before:bg-contain before:bg-no-repeat before:bg-right-top before:opacity-[27%] before:pointer-events-none before:z-0"
            >
              <h4 className="relative z-10 text-subtitle-3 font-bold text-[var(--color-primary-text)] line-clamp-2">
                {card.title}
              </h4>

              {card.description && (
                <p className="relative z-10 text-subtitle-4 font-semibold text-[var(--color-secondary-text)] opacity-[70%] line-clamp-2">
                  {card.description}
                </p>
              )}

              <Image
                src="/images/career-apply-arrow2.svg"
                alt="arrow"
                width={20}
                height={20}
                className="relative z-10 transition-all duration-300 ease-in-out group-hover:filter-[brightness(0)_saturate(100%)_invert(38%)_sepia(98%)_saturate(2000%)_hue-rotate(195deg)_brightness(95%)_contrast(105%)]"
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const InsightLeft: React.FC<InsightLeftProps> = ({
  links = [],
  bottomImages,
}) => {
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <div className="flex-1 w-full flex flex-col gap-2 justify-between">
      <div className="w-full h-full flex flex-wrap gap-[16px] lg:gap-[24px_15px] xl:gap-[24px]">
        {links.map((link) => (
          <InsightColumn
            key={link.id}
            link={link}
            isOpen={openId === link.id}
            onToggle={() =>
              setOpenId((current) => (current === link.id ? null : link.id))
            }
          />
        ))}
      </div>
      <SubmenuLeftBottom images={bottomImages} />
    </div>
  );
};

export default InsightLeft;
